import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useCancelOrder,
  useCompleteOrder,
  useConfirmOrder,
  useMarkOrderReady,
  useOrder,
  useOrders,
  useOrderStats,
  useStartProcessingOrder,
  useExport,
  useCreateOrder,
  useProducts,
} from '../../hooks';
import {
  ALL_TIME_START,
  ORDER_STATUS_GROUPS,
  aggregateStatusCounts,
  todayIso,
} from '../../utils/orderStatus';
import { useStores } from '../../hooks/useStores';
import { useStoreSelectionStore } from '../../stores';
import { useActiveCurrency } from '../../hooks/useActiveCurrency';
import { useCurrencyScope, useMoney } from '../../providers/CurrencyProvider';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { useReceiptsService } from '../../providers/ApiProvider';
import { isApiError } from '@conai/api-client';
import type { OrderItem } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import { Pagination, PageContainer, ExportButton, PageHeader } from '../../components/shared';
import {
  StatusPipelineCards,
  OrdersFilterBar,
  OrdersTable,
  OrdersInsightCard,
  SupplierPerformanceCard,
  type OrdersTableRow,
} from './components';

function PlusIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

type ListEnvelope<T> = {
  results?: T[];
  items?: T[];
  data?: T[] | { results?: T[]; items?: T[]; data?: T[]; count?: number };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

type OrderApiRow = {
  id: string;
  order_code?: string;
  order_number?: string;
  store_name?: string;
  store_code?: string;
  channel?: string;
  channel_display?: string;
  order_status?: string;
  status?: string;
  order_status_display?: string;
  payment_status?: string;
  payment_status_display?: string;
  total?: string | number;
  total_amount?: string | number;
  items_count?: number;
  created_at?: string;
};

type TFunc = (key: string, variables?: Record<string, string | number>) => string;

/** Dịch order status sang ngôn ngữ hiện tại — rỗng nếu status lạ (caller tự fallback). */
function translateOrderStatus(status: string, t: TFunc): string {
  const keyMap: Record<string, string> = {
    DRAFT: 'draft',
    PENDING_PAYMENT: 'pendingPayment',
    CONFIRMED: 'confirmed',
    PREPARING: 'preparing',
    READY: 'ready',
    PAID: 'paid',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    VOIDED: 'voided',
    REFUNDED: 'refunded',
    PARTIALLY_REFUNDED: 'partiallyRefunded',
  };
  const key = keyMap[status?.toUpperCase?.() ?? ''];
  return key ? t(key) : '';
}

/** Dịch payment status (PENDING/PAID/FAILED/REFUNDED...) — rỗng nếu lạ. */
function translatePaymentStatus(status: string, t: TFunc): string {
  const keyMap: Record<string, string> = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    PAID: 'paid',
    COMPLETED: 'completed',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
    REFUNDED: 'refunded',
  };
  const key = keyMap[status?.toUpperCase?.() ?? ''];
  return key ? t(key) : '';
}

// Khớp OrderStatus của BE — 'PROCESSING' không tồn tại, khâu chế biến là PREPARING.
//
// Bug #120: thẻ "Pending" ĐẾM cả DRAFT lẫn PENDING_PAYMENT (xem
// ORDER_STATUS_GROUPS), nhưng khi bấm vào lại chỉ LỌC mỗi DRAFT — số trên thẻ và
// số dòng dưới bảng không bao giờ khớp. Một thẻ giờ ánh xạ sang DANH SÁCH status
// đúng bằng nhóm nó đang đếm.
const pipelineToStatuses: Record<string, readonly string[]> = {
  pending: ORDER_STATUS_GROUPS.pending,
  confirmed: ['CONFIRMED'],
  processing: ['PREPARING'],
  ready: ORDER_STATUS_GROUPS.ready,
  completed: ORDER_STATUS_GROUPS.completed,
};

/** Danh sách status ứng với giá trị của filter `?status=` (có thể là nhiều status ngăn cách bằng dấu phẩy). */
function parseStatusFilter(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
}

function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const data = payload as ListEnvelope<T> | undefined;
  if (!data) return [];
  if (Array.isArray(data.data)) return data.data;
  if (data.data && typeof data.data === 'object') {
    return data.data.data ?? data.data.results ?? data.data.items ?? [];
  }
  return data.results ?? data.items ?? [];
}

function extractCount<T>(payload: unknown, fallback: number): number {
  if (Array.isArray(payload)) return payload.length;
  const data = payload as ListEnvelope<T> | undefined;
  if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    return data.data.count ?? data.count ?? data.total ?? data.meta?.count ?? fallback;
  }
  return data?.count ?? data?.total ?? data?.meta?.count ?? fallback;
}

function safeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function safeString(value: unknown, fallback = ''): string {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

function toTableRow(item: OrderApiRow): OrdersTableRow {
  const status = safeString(item.status || item.order_status, 'DRAFT').toUpperCase();
  return {
    id: String(item.id),
    code: safeString(item.order_code) || safeString(item.order_number) || String(item.id),
    storeName: safeString(item.store_name) || safeString(item.store_code) || 'Unknown Store',
    channelLabel: safeString(item.channel_display) || safeString(item.channel) || 'Unknown Channel',
    status,
    statusLabel: safeString(item.order_status_display) || status,
    paymentStatusLabel:
      safeString(item.payment_status_display) || safeString(item.payment_status) || 'Unknown Payment',
    itemsCount: Math.max(0, safeNumber(item.items_count, 0)),
    totalAmount: safeNumber(item.total, safeNumber(item.total_amount, 0)),
    createdAt: safeString(item.created_at),
  };
}

/**
 * Tên sản phẩm cho bảng "Tax per line" (bug #248).
 *
 * `OrderItemSerializer` đã nhúng sẵn `product_name` (snapshot lưu thẳng trên
 * `OrderItem` tại thời điểm bán), `product_sku` và `product_barcode` — BE xác
 * nhận 18/09/2026. Bảng này trước đây in ra UUID chỉ vì đọc nhầm field
 * `product` (object lồng, endpoint không trả) rồi rơi về nhánh dự phòng
 * `line.product_id`.
 *
 * Đọc `product_name` là xong. KHÔNG gọi thêm `/products/{id}/`: bản snapshot
 * mới là thứ cần hiện — tên sản phẩm có thể đã đổi sau ngày bán, mà hoá đơn
 * thì phải nói đúng cái đã bán hôm đó.
 *
 * `line.product?.name` giữ lại làm nhánh đầu cho các endpoint khác dùng chung
 * `OrderItem` và có nhúng object; không tốn gì.
 */
function OrderLineProductName({ line }: { line: OrderItem }) {
  const flat = line as OrderItem & {
    product_name?: string | null;
    product_sku?: string | null;
  };
  const name = line.product?.name ?? flat.product_name ?? null;
  const sku = line.product?.sku ?? flat.product_sku ?? null;

  if (name) {
    return (
      <span>
        {name}
        {sku ? <span className="ml-1 text-slate-400">({sku})</span> : null}
      </span>
    );
  }
  if (sku) return <span>{sku}</span>;
  // Chỉ còn lại id thì ghi rõ "ID:" — để không ai mang chuỗi đó sang màn
  // Products tra rồi không ra gì, đúng cái đã xảy ra ở bug #248.
  return <span className="text-slate-400">ID: {line.product_id}</span>;
}

export function OrdersPage() {
  const { language, t } = useI18n();
  const { selectedStoreId } = useStoreSelectionStore();

  // Tiền tệ theo cửa hàng đang chọn (sheet 2 #75). BE đã stamp
  // `Order.currency` lúc tạo đơn — khi payload trả field đó thì nên đọc của
  // từng đơn thay vì scope; hiện dùng scope cho các thẻ tổng của trang.
  useCurrencyScope(useActiveCurrency());
  const money = useMoney();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number.parseInt(searchParams.get('page') || '1', 10);
  const pipelineStatus = searchParams.get('pipeline') || null;
  const statusFilter = searchParams.get('status') || null;
  const storeFilter = searchParams.get('store') || null;
  const supplierFilter = searchParams.get('supplier') || null;
  const searchFilter = searchParams.get('search') || null;
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);

  // Cùng pattern trang Payments: filter Destination KHỞI TẠO theo store tổng
  // trên header (lần đầu mở, khi URL chưa có ?store=), sau đó filter của trang
  // toàn quyền — chọn "All Destinations" là xem tất cả, không rơi ngược về header.
  const storeInitRef = useRef(false);
  useEffect(() => {
    if (storeInitRef.current) return;
    if (searchParams.get('store')) {
      // URL đã có store (điều hướng/bookmark) — tôn trọng, không ghi đè.
      storeInitRef.current = true;
      return;
    }
    if (selectedStoreId) {
      storeInitRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.set('store', selectedStoreId);
      setSearchParams(next, { replace: true });
    }
  }, [selectedStoreId, searchParams, setSearchParams]);
  // PHÂN TRANG SERVER-SIDE: mỗi lần xem 1 trang = đúng 1 request /orders/.
  // (Bản cũ fetch-all lật hàng chục trang mỗi lần mở — DB nhiều đơn là bão request.)
  // Filter cũng đẩy xuống server: status (BE đã thêm alias), store, khoảng ngày.
  const pageSize = 10;

  // Nhóm status đang lọc — nguồn duy nhất cho cả param gửi lên lẫn lưới lọc client.
  const activeStatuses = useMemo(() => {
    const fromFilter = parseStatusFilter(statusFilter);
    if (fromFilter.length > 0) return fromFilter;
    if (pipelineStatus && pipelineToStatuses[pipelineStatus]) {
      return [...pipelineToStatuses[pipelineStatus]];
    }
    return [];
  }, [statusFilter, pipelineStatus]);

  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = { page, page_size: pageSize };

    // Filter của trang toàn quyền (đã init theo header ở trên) — không fallback.
    if (storeFilter) {
      params.store = storeFilter;
    }

    // Bug #120: `status` nhận danh sách phân tách bằng dấu phẩy (BE lọc tay, không
    // phải ChoiceFilter), và `meta.count` áp cùng bộ lọc nên số trên thẻ khớp số
    // dòng. Một tham số là đủ — không cần alias order_status/status__in nữa.
    if (activeStatuses.length > 0) {
      params.status = activeStatuses.join(',');
    }

    if (dateRange) {
      // Bug #113: mốc cuối phải là CUỐI ngày, gửi date thuần thì `lte` = 00:00 nên
      // lọc "1 ngày" không khớp đơn nào tạo sau nửa đêm.
      //
      // Bug #119: nhưng KHÔNG được đổi sang .toISOString(). Hàm đó quy đổi theo
      // múi giờ của MÁY NGƯỜI DÙNG; trình duyệt ở UTC+7 mà server ở châu Âu thì
      // cửa sổ lọc bị trượt vài tiếng và đơn của đúng ngày đó rơi ra ngoài — bảng
      // trống dù ngày chọn có đơn. Gửi chuỗi datetime KHÔNG kèm offset để server
      // hiểu theo múi giờ của chính nó, đúng nghĩa "các đơn trong ngày X".
      params.created_at__gte = `${dateRange.start}T00:00:00`;
      params.created_at__lte = `${dateRange.end}T23:59:59`;
    }

    if (searchFilter) {
      params.search = searchFilter;
    }

    return params;
  }, [page, storeFilter, activeStatuses, dateRange, searchFilter]);

  const { data: ordersData, isLoading: ordersLoading, error: ordersError } = useOrders(queryParams as any);
  const { data: storesData } = useStores({ page_size: 100 } as any);

  // Stats cards đếm qua /orders/stats/ — endpoint dùng chung get_queryset với
  // /orders/ (cùng scope, cùng soft-delete) nên số luôn khớp bảng bên dưới.
  // Cùng storeFilter với bảng: stats và danh sách không bao giờ lệch phạm vi.
  const effectiveStore = storeFilter;
  // Stats cards + Total Order Value theo ĐÚNG bộ lọc ngày của trang (nếu có),
  // để thẻ khớp với danh sách bên dưới. Không lọc ngày -> all-time.
  const { data: orderStatsData } = useOrderStats({
    store_id: effectiveStore ?? undefined,
    start_date: dateRange?.start ?? ALL_TIME_START,
    end_date: dateRange?.end ?? todayIso(),
  });

  // Dialog/Modal States
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isProposalOpen, setIsProposalOpen] = useState(false);
  useLockBodyScroll(isCreateOpen || isProposalOpen || !!selectedOrderId);

  // Hoá đơn điện tử của đơn (sheet 2 mục #51). Contract BE 29/08: receipt tự
  // sinh khi đơn settle; đơn cũ trước deploy chưa có -> 404 -> generate
  // (idempotent, admin/operator gọi được mọi đơn trong scope). Job async trả
  // PENDING không kèm pdf_url -> poll ngắn rồi mở tab mới.
  const receiptsService = useReceiptsService();
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const handleViewInvoice = async (orderId: string) => {
    if (invoiceLoading) return;
    setInvoiceLoading(true);
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const tryGetByOrder = async () => {
      try {
        return await receiptsService.getByOrder(orderId);
      } catch (err) {
        if (isApiError(err) && err.status === 404) return null;
        throw err;
      }
    };
    try {
      let receipt = await tryGetByOrder();
      if (!receipt) {
        receipt = await receiptsService.generateByOrder(orderId);
      }
      for (let attempt = 0; receipt?.status === 'PENDING' && attempt < 4; attempt++) {
        await sleep(2000);
        receipt = (await tryGetByOrder()) ?? receipt;
      }
      const url = receipt?.pdf_url ?? receipt?.receipt_url ?? null;
      if (receipt?.status === 'FAILED') {
        toast.error(t('invoiceFailed'));
      } else if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        toast(t('invoicePreparing'));
      }
    } catch {
      toast.error(t('invoiceFailed'));
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Form states for creating order
  const [newOrderForm, setNewOrderForm] = useState({
    storeId: '',
    itemsCount: 1,
    totalAmount: 100,
  });

  const confirmOrder = useConfirmOrder();
  const cancelOrder = useCancelOrder();
  const startProcessingOrder = useStartProcessingOrder();
  const markOrderReady = useMarkOrderReady();
  const completeOrder = useCompleteOrder();
  const createOrder = useCreateOrder();
  const { data: productsData } = useProducts({ page_size: 10 });

  // Server đã lọc + phân trang; FE chỉ map row hiển thị.
  const paginatedRows = useMemo(
    () => extractList<OrderApiRow>(ordersData).map(toTableRow),
    [ordersData]
  );

  // Tổng khớp filter, do server đếm — không phải số row của trang hiện tại.
  const totalCount = extractCount<OrderApiRow>(ordersData, paginatedRows.length);
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);

  // Phân nhóm status_counts (BE zero-fill đủ 11 status) qua utils dùng chung.
  const stats = useMemo(() => {
    const agg = aggregateStatusCounts(orderStatsData?.status_counts);
    return {
      pending: agg.pending,
      confirmed: agg.counts['CONFIRMED'] ?? 0,
      processing: agg.counts['PREPARING'] ?? 0,
      ready: agg.ready,
      completed: agg.completed,
      cancelled: agg.cancelled,
      // countable_orders do BE tính theo ORDER_STATUSES_COUNTABLE — nguồn chuẩn.
      total: orderStatsData?.countable_orders ?? agg.total,
    };
  }, [orderStatsData]);

  // Doanh thu đã thu (COMPLETED + PAID) do BE tính — không cộng client-side từ
  // các row fetch được nữa (thiếu trang là sai số ngay).
  const totalValue = safeNumber(orderStatsData?.revenue, 0);

  const storeOptions = useMemo(
    () => extractList<{ id: string; name: string }>(storesData).map((s) => ({ id: s.id, name: s.name })),
    [storesData]
  );

  const handlePipelineClick = (status: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (status) {
      // Bug #120: một thẻ có thể ứng với nhiều status (Pending = DRAFT +
      // PENDING_PAYMENT) — ghi cả nhóm vào URL, không cắt còn mỗi status đầu.
      const mapped = pipelineToStatuses[status];
      next.set('status', mapped ? mapped.join(',') : status.toUpperCase());
      next.delete('pipeline');
    } else {
      next.delete('status');
      next.delete('pipeline');
    }
    next.set('page', '1');
    setSearchParams(next);
  };

  // Bug #119: đổi khoảng ngày mà không reset trang — đang ở trang 3, lọc còn 2
  // đơn thì bảng trống trơn dù dữ liệu có. Mọi filter khác đều đã reset về
  // trang 1, riêng bộ lọc ngày bị bỏ sót vì nối thẳng setDateRange vào prop.
  const handleDateRangeChange = (range: { start: string; end: string } | null) => {
    setDateRange(range);
    const next = new URLSearchParams(searchParams);
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleFilterChange = (key: string, value: string | null) => {
    // User đã tự đụng filter store — khoá init từ header, kể cả khi chọn "All".
    if (key === 'store') storeInitRef.current = true;
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleClearFilters = () => {
    storeInitRef.current = true;
    setSearchParams({ page: '1' });
    setDateRange(null);
  };

  const handlePageChange = (newPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(newPage));
    setSearchParams(next);
  };

  const handleApprove = async (id: string) => {
    const row = paginatedRows.find((item) => item.id === id);
    if (!row) return;

    if (row.status === 'DRAFT' || row.status === 'PENDING_PAYMENT') {
      await confirmOrder.mutateAsync(id);
      return;
    }
    if (row.status === 'CONFIRMED') {
      await startProcessingOrder.mutateAsync(id);
      return;
    }
    if (row.status === 'PREPARING') {
      await markOrderReady.mutateAsync(id);
      return;
    }
    if (row.status === 'READY') {
      await completeOrder.mutateAsync(id);
    }
  };

  const handleReject = async (id: string) => {
    await cancelOrder.mutateAsync({ id, reason: 'Cancelled by admin' });
  };

  // Handle mock create order
  // Handle create order mutation
  const handleCreateOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!newOrderForm.storeId) {
        toast.error(t('validationPleaseSelectStore'));
        return;
      }
      const products = extractList<{ id: string; price?: string | number }>(productsData);
      if (products.length === 0) {
        toast.error(t('noProductsAvailableToCreateOrder'));
        return;
      }

      // Select first product for mock creation request
      const product = products[0];
      const unitPrice = typeof product.price === 'number' ? String(product.price) : (product.price || '10');

      await createOrder.mutateAsync({
        store_id: newOrderForm.storeId,
        channel: 'WEB',
        items: [
          {
            product_id: product.id,
            quantity: newOrderForm.itemsCount,
            unit_price: unitPrice,
          }
        ]
      });
      setIsCreateOpen(false);
    } catch (err) {
      // Hiện message thật từ server thay vì nuốt lỗi (bug #102) — để biết
      // nguyên nhân BE từ chối (vd: product không thuộc store, channel không hợp lệ).
      let message = t('failedToCreateOrder');
      if (err && typeof err === 'object') {
        const e2 = err as { message?: string; originalError?: { response?: { data?: any } } };
        const raw = e2.originalError?.response?.data;
        if (raw?.error?.message) message = raw.error.message;
        else if (raw?.message) message = raw.message;
        else if (raw?.errors) {
          message = Object.entries(raw.errors)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('; ');
        } else if (e2.message) message = e2.message;
      }
      toast.error(message);
    }
  };

  const selectedOrder = useMemo(() => {
    return paginatedRows.find(o => o.id === selectedOrderId);
  }, [paginatedRows, selectedOrderId]);

  // Danh sách dòng chỉ có ở bản CHI TIẾT — bản trong danh sách không kèm items.
  // Chỉ gọi khi modal mở để không thêm một request cho mỗi lượt vào trang.
  const { data: selectedOrderDetail } = useOrder(selectedOrderId ?? '', {
    enabled: !!selectedOrderId,
  });
  const orderLines = useMemo(
    () => (selectedOrderDetail as { items?: OrderItem[] } | undefined)?.items ?? [],
    [selectedOrderDetail]
  );

  const exportParams = useMemo(() => {
    const p: Record<string, string | number | undefined> = {};
    if (storeFilter) p.store = storeFilter;
    // Export dùng chung get_queryset với /orders/ nên tham số phải giống hệt.
    if (activeStatuses.length > 0) p.status = activeStatuses.join(',');
    if (dateRange) {
      p.created_at__gte = `${dateRange.start}T00:00:00`;
      p.created_at__lte = `${dateRange.end}T23:59:59`;
    }
    return p;
  }, [storeFilter, activeStatuses, dateRange]);

  const { triggerExport, isExporting } = useExport({ endpoint: '/orders/', params: exportParams, filePrefix: 'orders' });

  // Thẻ nào đang được chọn: so khớp theo TẬP status, không so chuỗi — nhờ vậy
  // "DRAFT,PENDING_PAYMENT" vẫn làm sáng đúng thẻ Pending (bug #120).
  const activePipelineCard = useMemo(() => {
    if (activeStatuses.length === 0) return undefined;
    const selected = new Set(activeStatuses);
    for (const [card, statuses] of Object.entries(pipelineToStatuses)) {
      if (statuses.length === selected.size && statuses.every((status) => selected.has(status))) {
        return card;
      }
    }
    return undefined;
  }, [activeStatuses]);

  return (
    <PageContainer>
      <PageHeader
        title={t('orders')}
        sectionLabel={t('orderManagement')}
        subtitle={t('manageCustomerOrdersAndMonitorFulfi')}
        actions={
          <div className="flex items-center gap-3">
            <ExportButton onExport={triggerExport} isLoading={isExporting} />
            <button onClick={() => setIsCreateOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/25 transition-all hover:shadow-xl hover:shadow-primary-500/30">
              <PlusIcon />
              {t('createOrder')}
            </button>
          </div>
        }
      />

      <div className="mt-8 mb-6">
        <StatusPipelineCards
          stats={stats}
          activeStatus={activePipelineCard}
          onStatusClick={handlePipelineClick}
          isLoading={ordersLoading}
        />
      </div>

      {totalValue > 0 && (
        <div className="mb-6 rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('totalOrderValue')}</span>
              <p className="mt-1 text-2xl font-extrabold">
                {money.format(totalValue, { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <OrdersFilterBar
          selectedStore={storeFilter}
          selectedStatus={statusFilter}
          selectedSupplier={supplierFilter}
          dateRange={dateRange}
          onStoreChange={(v) => handleFilterChange('store', v)}
          onStatusChange={(v) => handleFilterChange('status', v)}
          onSupplierChange={(v) => handleFilterChange('supplier', v)}
          onDateRangeChange={handleDateRangeChange}
          onClearFilters={handleClearFilters}
          stores={storeOptions}
          suppliers={[]}
        />
      </div>

      {ordersError && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-semibold">Error loading orders</p>
          <p className="mt-1 text-sm">Please try again or contact support if the problem persists.</p>
        </div>
      )}

      <div className="mb-6">
        <OrdersTable
          orders={paginatedRows}
          isLoading={ordersLoading}
          onApprove={handleApprove}
          onReject={handleReject}
          onView={(id) => {
            setSelectedOrderId(id);
          }}
          onRowClick={(order) => {
            setSelectedOrderId(order.id);
          }}
        />
      </div>

      {totalCount > 0 && (
        <div className="mb-8">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={10}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 font-manrope">
        <div className="lg:col-span-8">
          <OrdersInsightCard
            variant="light"
            onClick={() => setIsProposalOpen(true)}
          />
        </div>
        <div className="lg:col-span-4">
          <SupplierPerformanceCard />
        </div>
      </div>

      {/* Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => setSelectedOrderId(null)}>
          <div className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-slate-900">{t('orderDetails') || 'Order Details'}</h3>
              <button onClick={() => setSelectedOrderId(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="border-t border-slate-100 px-6 py-4 space-y-4 text-sm text-slate-700">
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">{t('orderNumber') || 'Order Number'}:</span>
                <span className="font-bold text-slate-900">#{selectedOrder.code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">{t('fulfillmentStatus') || 'Fulfillment Status'}:</span>
                <span className="font-semibold text-primary-700 uppercase">
                  {translateOrderStatus(selectedOrder.status, t) || selectedOrder.statusLabel}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">{t('paymentStatus') || 'Payment Status'}:</span>
                <span className="font-semibold text-emerald-700 uppercase">
                  {translatePaymentStatus(selectedOrder.paymentStatusLabel, t) || selectedOrder.paymentStatusLabel}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">{t('destinationStore') || 'Destination Store'}:</span>
                <span className="font-semibold text-slate-900">{selectedOrder.storeName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">{t('fulfillmentChannel') || 'Fulfillment Channel'}:</span>
                <span className="text-slate-600">{selectedOrder.channelLabel}</span>
              </div>
              <div className="flex justify-between">
                {/* itemsQuantity là nhãn tĩnh; itemsCount là chuỗi có {{count}} — gọi
                    không truyền count sẽ hiện nguyên "{{ count }} pozycji" */}
                <span className="text-slate-500 font-medium">{t('itemsQuantity')}:</span>
                <span className="font-bold text-slate-900">{selectedOrder.itemsCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">{t('totalAmount') || 'Total Amount'}:</span>
                <span className="font-extrabold text-slate-950">{money.format(selectedOrder.totalAmount, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">{t('orderDate') || 'Order Date'}:</span>
                <span className="text-slate-600">{new Date(selectedOrder.createdAt).toLocaleString()}</span>
              </div>

              {/* Thuế theo TỪNG DÒNG (mục #77). BE bổ sung `tax_rate` cạnh
                  `tax_amount` trên order item để đối chiếu được ngay tại đây.
                  Giá đã GỒM thuế; tổng thuế đơn là tổng các dòng ĐÃ LÀM TRÒN,
                  nên nó lệch vài xu so với thuế tính trên tổng — lệch đó đúng.
                  Client KHÔNG tính lại, chỉ hiện số server trả. */}
              {orderLines.length > 0 && (
                <div className="mt-2 border-t border-slate-100 pt-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('orderTaxByLine')}
                  </p>
                  <table className="w-full text-xs">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="py-1 text-left font-semibold">{t('product')}</th>
                        <th className="w-16 py-1 text-right font-semibold">{t('quantity')}</th>
                        <th className="w-16 py-1 text-right font-semibold">%</th>
                        <th className="w-24 py-1 text-right font-semibold">{t('orderLineTax')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {orderLines.map((line, i) => (
                        <tr key={line.id ?? `${line.product_id}-${i}`}>
                          <td className="py-1.5 text-slate-700">
                            <OrderLineProductName line={line} />
                          </td>
                          <td className="py-1.5 text-right text-slate-600">{line.quantity}</td>
                          <td className="py-1.5 text-right text-slate-600">
                            {line.tax_rate != null ? Number(line.tax_rate) : '—'}
                          </td>
                          <td className="py-1.5 text-right font-semibold text-slate-900">
                            {line.tax_amount != null ? money.format(Number(line.tax_amount)) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-[11px] text-slate-400">{t('orderTaxByLineHint')}</p>
                </div>
              )}
            </div>
            <div className="bg-slate-50 px-6 py-4 flex justify-end gap-2 border-t border-slate-100">
              <button onClick={() => setSelectedOrderId(null)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                {t('close') || 'Close'}
              </button>
              {/* Xem hoá đơn (mục #51) — mở PDF tab mới, poll khi job đang sinh */}
              <button
                onClick={() => handleViewInvoice(selectedOrder.id)}
                disabled={invoiceLoading}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors disabled:opacity-60"
              >
                {invoiceLoading ? t('invoiceLoading') : t('viewInvoice')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Order Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => setIsCreateOpen(false)}>
          <form onSubmit={handleCreateOrderSubmit} className="w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-6 pb-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">{t('createNewOrder')}</h3>
              <button type="button" onClick={() => setIsCreateOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="border-t border-slate-100 px-6 py-4 space-y-4 text-sm">
              <div className="flex flex-col gap-1.5">
                <label className="font-semibold text-slate-700">{t('selectStore')}</label>
                <select 
                  required
                  value={newOrderForm.storeId} 
                  onChange={(e) => setNewOrderForm({ ...newOrderForm, storeId: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('chooseStore')}</option>
                  {storeOptions.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="font-semibold text-slate-700">{t('itemsQuantity')}</label>
                <input 
                  type="number" 
                  min="1" 
                  required
                  value={newOrderForm.itemsCount} 
                  onChange={(e) => setNewOrderForm({ ...newOrderForm, itemsCount: Number(e.target.value) })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                {/* Bug #53 (cùng bệnh): chuỗi dịch trước đây nhúng "($)" trong khi
                    cả trang Orders hiển thị ₴. Ký hiệu lấy từ utils/currency. */}
                <label className="font-semibold text-slate-700">{t('totalPriceCurrency')} ({money.symbol})</label>
                <input 
                  type="number" 
                  min="1" 
                  required
                  value={newOrderForm.totalAmount} 
                  onChange={(e) => setNewOrderForm({ ...newOrderForm, totalAmount: Number(e.target.value) })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-100">
              <button type="button" onClick={() => setIsCreateOpen(false)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                {t('cancel')}
              </button>
              <button type="submit" className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors">
                {t('submitOrder')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Review Proposal Modal */}
      {isProposalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => setIsProposalOpen(false)}>
          <div className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-6 pb-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  {language === 'pl' ? 'Propozycja Optymalizacji AI' : language === 'uk' ? 'Пропозиція AI-оптимізації' : 'AI Optimization Proposal'}
                </h3>
              </div>
              <button onClick={() => setIsProposalOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="border-t border-slate-100 px-6 py-4 text-sm text-slate-600 space-y-4">
              <p className="leading-relaxed">
                {language === 'pl'
                  ? 'Po przeanalizowaniu rzeczywistego przepływu ruchu, model uczenia maszynowego ConAI sugeruje konsolidację zamówień wysyłkowych według lokalizacji, co pozwala zaoszczędzić +14% kosztów logistycznych.'
                  : language === 'uk'
                  ? 'Проаналізувавши реальні транспортні потоки, модель машинного навчання ConAI пропонує об’єднати замовлення на доставку за географічними кластерами, що дозволить заощадити +14% логістичних витрат.'
                  : 'After analyzing actual traffic flow, the ConAI machine learning model suggests consolidating shipping orders by location clusters to save +14% in logistics costs.'}
              </p>
              <div className="rounded-xl bg-slate-50 p-4 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-500">
                    {language === 'pl' ? 'Przewidywane oszczędności:' : language === 'uk' ? 'Очікувана місячна економія:' : 'Expected Monthly Savings:'}
                  </span>
                  <span className="font-bold text-emerald-600">{money.format(1450)} / month</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-slate-500">
                    {language === 'pl' ? 'Sugerowana grupa zamówień:' : language === 'uk' ? 'Пропоноване групування замовлень:' : 'Suggested Grouping Orders:'}
                  </span>
                  <span className="font-bold text-slate-700">
                    {language === 'pl' ? '4 zamówienia (Obszar Manhattan)' : language === 'uk' ? '4 замовлення (регіон Манхеттен)' : '4 orders (Manhattan Region)'}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-100">
              <button onClick={() => setIsProposalOpen(false)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                {language === 'pl' ? 'Zamknij' : language === 'uk' ? 'Закрити' : 'Close'}
              </button>
              <button onClick={() => { setIsProposalOpen(false); toast.success(language === 'pl' ? 'Propozycja została pomyślnie zastosowana!' : language === 'uk' ? 'Пропозицію успішно застосовано!' : 'Proposal applied successfully!'); }} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors">
                {language === 'pl' ? 'Zastosuj Propozycję' : language === 'uk' ? 'Застосувати пропозицію' : 'Apply Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
